import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CONFIG, ERROR_MESSAGES, SUCCESS_MESSAGES } from '../constants/config';
import { formatCurrency, truncateText, getRankClass, getProfilePicSrc, validateAmount, sanitizeAmount } from '../utils/validation';
import { ApiService, PaymentService } from '../services/api';
import CurrencyService from '../utils/currencyService';

const SubmissionCard = ({ submission, rank, onDonate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(CONFIG.DEFAULT_PAYMENT_AMOUNT);
  const [paymentError, setPaymentError] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showPixelArtModal, setShowPixelArtModal] = useState(false);
  const [paymentRegion, setPaymentRegion] = useState('us');
  const [currencyInfo, setCurrencyInfo] = useState(null);

  // Detect user's region and get live currency rates
  useEffect(() => {
    const detectRegionAndRates = async () => {
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        let region = 'us'; // default

        console.log('🕐 Detected timezone:', timezone);

        // European timezones - expanded list
        const europeanTimezones = [
          'Europe/', 'GMT', 'UTC', 'WET', 'CET', 'EET'
        ];
        
        if (europeanTimezones.some(tz => timezone.includes(tz))) {
          region = 'eu';
          console.log('🇪🇺 European timezone detected, setting region to EU');
        }

        // Try browser language as secondary indicator
        const language = navigator.language || navigator.languages[0];
        console.log('🗣️ Detected language:', language);
        
        const europeanLanguages = ['de', 'fr', 'nl', 'es', 'it', 'pt', 'pl', 'sv', 'da', 'no', 'fi'];
        if (europeanLanguages.some(lang => language?.toLowerCase().startsWith(lang))) {
          region = 'eu';
          console.log('🇪🇺 European language detected, setting region to EU');
        }

        // Manual override for Netherlands specifically
        if (timezone.includes('Amsterdam') || language?.toLowerCase().includes('nl')) {
          region = 'eu';
          console.log('🇳🇱 Netherlands detected, ensuring EU region');
        }

        console.log('🌍 Final detected region:', region);
        setPaymentRegion(region);

        // Get live currency info from the service
        const currencyInfo = await CurrencyService.getCurrencyInfo(region);
        setCurrencyInfo(currencyInfo);
        console.log('💱 Currency info loaded:', currencyInfo);

      } catch (error) {
        console.error('❌ Region detection failed:', error);
        setPaymentRegion('us');
        setCurrencyInfo({
          currency: 'USD',
          rate: 1.0,
          symbol: '$',
          isLive: false
        });
      }
    };

    detectRegionAndRates();
  }, []);

  const handleCardClick = (e) => {
    // Don't expand if clicking on donate button, external links, or profile picture
    if (e.target.closest('.btn-donate') || e.target.closest('.external-link') || e.target.closest('.profile-pic-container')) {
      return;
    }
    setIsExpanded(!isExpanded);
  };

  const handlePixelArtClick = (e) => {
    e.stopPropagation();
    setShowPixelArtModal(true);
  };

  const closePixelArtModal = () => {
    setShowPixelArtModal(false);
  };

  const handleDonate = async (e) => {
    e.stopPropagation();
    
    // Clear any previous error
    setPaymentError('');
    
    // Validate minimum amount (convert to number for validation)
    const amountNum = parseFloat(paymentAmount);
    if (!validateAmount(amountNum) || paymentAmount === '' || isNaN(amountNum)) {
      setPaymentError(ERROR_MESSAGES.MINIMUM_PAYMENT);
      return;
    }
    
    try {
      setIsProcessingPayment(true);
      
      // Check if we're in testing mode
      if (CONFIG.TESTING_MODE.ENABLED) {
        // Testing mode - use direct payment API
        await ApiService.createPayment(submission.id, amountNum);
        
        // Show success message
        alert(`${SUCCESS_MESSAGES.PAYMENT_SUCCESS}\n\nYou paid ${formatCurrency(amountNum)} to "${submission.title}"`);
        
        // Reset payment amount
        setPaymentAmount(CONFIG.DEFAULT_PAYMENT_AMOUNT);
        
        // Refresh the page to show updated amounts
        window.location.reload();
      } else {
        // Production mode - use Stripe payment flow
        console.log('🌍 Payment region detected:', paymentRegion);
        console.log('💱 Currency info:', currencyInfo);
        console.log('💰 Amount in USD:', amountNum);
        
        const checkoutResponse = await ApiService.createCheckoutSession(
          submission.id, // Existing submission ID
          amountNum,
          null, // No new submission data
          paymentRegion // Pass detected payment region
        );
        
        console.log('✅ Checkout response:', checkoutResponse);

        // Redirect to Stripe Checkout
        await PaymentService.processStripePayment(checkoutResponse.session_id);
        
        // If we reach here, something went wrong (Stripe should redirect)
        // The webhook will handle updating the payment amount after successful payment
      }
    } catch (err) {
      console.error('Payment error:', err);
      setPaymentError('Failed to process payment. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return (
    <div 
      className={`submission-card ${getRankClass(rank)}`}
      onClick={handleCardClick}
    >
      <div className="submission-header">
        <div className="rank-number">#{rank}</div>
        
        <div 
          className="profile-pic-container"
          onClick={getProfilePicSrc(submission.profile_pic_url) ? handlePixelArtClick : undefined}
          style={{ 
            cursor: getProfilePicSrc(submission.profile_pic_url) ? 'pointer' : 'default',
            position: 'relative'
          }}
          title={getProfilePicSrc(submission.profile_pic_url) ? 'Click to view pixel art' : undefined}
        >
          {getProfilePicSrc(submission.profile_pic_url) ? (
            <img 
              src={getProfilePicSrc(submission.profile_pic_url)}
              alt={`${submission.title} profile`}
              className="profile-pic"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div 
            className="profile-pic-placeholder"
            style={{ display: getProfilePicSrc(submission.profile_pic_url) ? 'none' : 'flex' }}
          >
            {submission.title.charAt(0).toUpperCase()}
          </div>
        </div>

        <div className="submission-info">
          <h3 className="submission-title">
            {submission.url ? (
              <a 
                href={submission.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="external-link"
                onClick={(e) => e.stopPropagation()}
              >
                {submission.title}
              </a>
            ) : (
              submission.title
            )}
          </h3>
          <div className="submission-amount">
            {formatCurrency(submission.total_donated)}
          </div>
        </div>

        <div className="payment-section" style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-end' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <span style={{
                color: '#ffffff',
                fontSize: '0.9rem',
                fontWeight: 'bold'
              }}>
                $
              </span>
            <input
              type="number"
              step="0.01"
                value={paymentAmount}
              onChange={(e) => {
                const sanitizedValue = e.target.value === '' ? '' : sanitizeAmount(e.target.value);
                  setPaymentAmount(sanitizedValue);
                  setPaymentError(''); // Clear error when user types
              }}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '80px',
                padding: '6px 8px',
                  border: paymentError ? '1px solid #ff4444' : '1px solid #00ffff',
                borderRadius: '4px',
                background: 'rgba(0, 26, 46, 0.8)',
                color: '#ffffff',
                fontSize: '0.9rem'
              }}
            />
            </div>
            <button 
              onClick={handleDonate}
              className="btn btn-donate"
              disabled={isProcessingPayment}
            >
              {isProcessingPayment ? 'Processing...' : 'Boost'}
            </button>
            

          </div>
          

          
          {paymentError && (
            <div style={{
              color: '#ff4444',
              fontSize: '0.8rem',
              marginTop: '5px',
              textAlign: 'right'
            }}>
              {paymentError}
            </div>
          )}
        </div>
      </div>

      <div className="submission-description">
        {isExpanded ? (
          <div className="description-expanded">
            {submission.description}
          </div>
        ) : (
          <div>
            {truncateText(submission.description)}
            {submission.description.length > CONFIG.DESCRIPTION_TRUNCATE_LENGTH && (
              <span className={`expand-indicator ${isExpanded ? 'expanded' : ''}`}>
                ▼
              </span>
            )}
          </div>
        )}
      </div>

      {submission.description.length > CONFIG.DESCRIPTION_TRUNCATE_LENGTH && (
        <div style={{ 
          textAlign: 'center', 
          marginTop: '10px', 
          fontSize: '0.8rem', 
          color: '#00ffff',
          opacity: 0.7
        }}>
          {isExpanded ? 'Click to collapse' : 'Click to expand'}
        </div>
      )}

      {/* Pixel Art Modal - Rendered at document body level */}
      {showPixelArtModal && getProfilePicSrc(submission.profile_pic_url) && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(4px)'
          }}
          onClick={closePixelArtModal}
        >
          <div 
            style={{
              backgroundColor: '#001a2e',
              border: '2px solid #00ffff',
              borderRadius: '8px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '15px',
              boxShadow: '0 0 20px rgba(0, 255, 255, 0.3)',
              maxWidth: '90vw',
              maxHeight: '90vh'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ 
              color: '#00ffff', 
              margin: 0, 
              fontSize: '1.2rem',
              textAlign: 'center',
              fontFamily: 'Press Start 2P, monospace'
            }}>
              {submission.title} - Pixel Art
            </h3>
            
            <div style={{
              border: '2px solid #444',
              borderRadius: '4px',
              padding: '4px',
              backgroundColor: '#222'
            }}>
              <img 
                src={getProfilePicSrc(submission.profile_pic_url)}
                alt={`${submission.title} pixel art`}
                style={{
                  width: '200px',
                  height: '200px',
                  imageRendering: 'pixelated',
                  imageRendering: '-moz-crisp-edges',
                  imageRendering: 'crisp-edges',
                  border: '1px solid #666',
                  display: 'block'
                }}
              />
            </div>
            
            <div style={{
              fontSize: '0.9rem',
              color: '#aaa',
              textAlign: 'center',
              fontFamily: 'Press Start 2P, monospace'
            }}>
              Original Size: 50×50 pixels
            </div>
            
            <button 
              onClick={closePixelArtModal}
              style={{
                padding: '8px 16px',
                backgroundColor: '#00ffff',
                color: '#001a2e',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                fontFamily: 'Press Start 2P, monospace'
              }}
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SubmissionCard; 